import { Component, HostListener, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TWO_MINUTES_IN_MILLIS } from '@core/consts';
import { endOfDay, mapId, mapIds, startOfDay } from '@core/utils/filters-utils';
import { FuseProgressBarService } from '@fuse/components/progress-bar/progress-bar.service';
import { FuseTranslationLoaderService } from '@fuse/services/translation-loader.service';
import { DateIntervalValidation } from '@validators/date-interval-validation';
import { DateValidation } from '@validators/date.validation';
import { isEqual } from 'lodash';
import * as moment from 'moment';
import { Subject } from 'rxjs';
import { debounceTime, filter, switchMap, takeUntil, tap } from 'rxjs/operators';
import { sentry } from 'sentry';
import { LineChartData } from '../../../core/components/charts/line/line.component';
import { PieChartItem } from '../../../core/components/charts/pie/pie.component';
import { StorageMovement } from '../../../core/interfaces/storage-movements';
import { delay } from '../../../core/utils/delay';
import english from './i18n/en.json';
import spanish from './i18n/es.json';
import portuguese from './i18n/pt.json';
import { DashboardStorageMovementsMapperService } from './services/dashboard-storage-movements.mapper.service';
import { DashboardStorageMovementsService, MovementsByDay, MovementsByType } from './services/dashboard-storage-movements.service';

@Component({
  templateUrl: './storage-movements.component.html',
  styleUrls: ['./storage-movements.component.scss'],
  encapsulation: ViewEncapsulation.Emulated
})
export class DashboardStorageMovementsComponent implements OnInit, OnDestroy {

  public isLoading = true;
  public isFilterOn = false;
  public totalMaintenances = 0;
  public isFilterExpanded = false;
  public accountMaintenancesStatusInterval = {
    responseAverage: '',
    solutionAverage: '',
    pending: '',
    scheduled: '',
    inProgress: '',
    reported: '',
    requested: '',
    onNegotiation: '',
  };
  public accountMaintenancesVolumeCount: any;

  public movementsByType: MovementsByType = {
    entryCount: 0,
    removeCount: 0,
    transferCount: 0,
  };

  private intervalId: any;
  private subscriptions = new Subject<void>();

  private offset = 0;
  private readonly limit = 100;

  public hasAccountStorageMovementsByDayData = false;

  private defaultFilterValue = {
    from: moment().subtract(1, 'month'),
    to: moment(),
    product: null,
    storages: null,
  };

  public filterForm = new FormGroup({
    from: new FormControl(this.defaultFilterValue.from, [Validators.required, DateValidation.validate]),
    to: new FormControl(this.defaultFilterValue.to, [Validators.required, DateValidation.validate]),
    product: new FormControl(this.defaultFilterValue.product),
    storages: new FormControl(this.defaultFilterValue.storages),
  }, {
    validators: [
      DateIntervalValidation.validate({
        from: 'from',
        to: 'to',
        maxDiffLte: 1,
        unit: 'year'
      }),
    ]
  });

  public productByStorage: PieChartItem[] = [];
  public movementsByDay: MovementsByDay[] = [];
  public movementsByDayTimeline: LineChartData[] = [];
  public movements: StorageMovement[] = [];

  constructor(
    private readonly fuseTranslationLoaderService: FuseTranslationLoaderService,
    private readonly fuseProgressBarService: FuseProgressBarService,
    private readonly dashboardStorageMovementsService: DashboardStorageMovementsService,
    private readonly chartDateIntervalSelectorMapperService: DashboardStorageMovementsMapperService,
  ) {
    this.fuseTranslationLoaderService.loadJsonTranslations({ english, portuguese, spanish });
  }

  ngOnInit(): void {
    this.updateAutoRefreshState({ enabled: false });

    this.filterForm.valueChanges.pipe(
      takeUntil(this.subscriptions),
      filter(() => this.filterForm.valid),
      debounceTime(200),
      tap(() => this.showLoading()),
      tap(() => {
        const currentWhere = this.toQuery(this.filterForm.value);
        const defaultWhere = this.toQuery(this.defaultFilterValue);
        this.isFilterOn = !isEqual(currentWhere, defaultWhere);
      }),
      switchMap(() => this.updateData()),
      tap(() => this.checkIfThereIsData()),
      tap(() => this.hideLoading())
    ).subscribe();

    this.reload();
    this.checkIfThereIsData();
  }

  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
    clearInterval(this.intervalId);
  }

  private toQuery(value: any): any {
    return {
      from: startOfDay(value.from),
      to: endOfDay(value.to),
      productId: mapId(value.product),
      storageId_in: mapIds(value.storages),
    };
  }

  private async updateData(): Promise<void> {
    try {
      const where = this.toQuery(this.filterForm.value);

      const [
        movementsByType,
        productByStorage,
        movementsByDay,
        movements,
      ] = await Promise.all([
        this.dashboardStorageMovementsService.accountStorageMovementsByType(where),
        where.productId && this.dashboardStorageMovementsService.accountProductsByStorage(where),
        where.productId && this.dashboardStorageMovementsService.accountStorageMovementsByDay(where),
        where.productId && this.dashboardStorageMovementsService.storageMovements(where, this.limit, this.offset),
        delay(700)
      ]);

      this.movementsByType = movementsByType;
      this.productByStorage = productByStorage;
      this.movementsByDay = movementsByDay;

      this.movements = movements;

      const maintenanceCompletedTimeline = this.chartDateIntervalSelectorMapperService.toLineChartFormat({
        movementsByDay: movementsByDay,
        from: where.from,
        to: where.to,
      });

      this.movementsByDayTimeline = maintenanceCompletedTimeline;
    } catch (err) {
      console.error(err);
      sentry.captureException(err);
    }
  }

  updateAutoRefreshState({ enabled }: { enabled: boolean }): void {
    if (enabled) {
      this.intervalId = setInterval(() => this.reload(), TWO_MINUTES_IN_MILLIS);
    } else {
      clearInterval(this.intervalId);
    }
  }

  clearFilters(): void {
    this.filterForm.setValue(this.defaultFilterValue);
  }

  private showLoading(): void {
    this.isLoading = true;
    this.fuseProgressBarService.show();
  }

  private hideLoading(): void {
    this.isLoading = false;
    this.fuseProgressBarService.hide();
  }

  public reload(): void {
    this.filterForm.updateValueAndValidity();
  }

  @HostListener('window:beforeprint')
  async beforePrint(): Promise<void> {
    this.isFilterExpanded = true;
  }

  private checkIfThereIsData(): void {
    this.hasAccountStorageMovementsByDayData = this.movementsByDay && this.movementsByDay.some((item: any) => item.finalCount);
  }
}
